import React, { useState, useEffect, useRef } from 'react';
import SelectBox from 'devextreme-react/select-box';
import Button from 'devextreme-react/button';
import { ActionBtns, DataPopup, LabeledItem } from '@core/components';
import { useOnChangeEffect, useTranslation } from '@core/hooks';
import { XDevExtreme, XUserInteraction, XData } from '@core/utils';
import { AxiosPromiseLike } from '@core/utils/src/devExtreme/customDataSource/types';
import DataSource from 'devextreme/data/data_source';

type RecordId = string | number | null | undefined;

type Record = {
  Id: RecordId;
};

type SelectDataSourceStore = {
  store: unknown;
  [key: string]: unknown;
};

type SelectDataSourceReturn = unknown[] | SelectDataSourceStore;

type SelectDataSource = (() => SelectDataSourceStore) | SelectDataSourceReturn;

export type AdditionSelectProps<TRecord> = {
  recordId: RecordId;
  setRecordId: (recordId: RecordId) => void;
  popupTitle: string;
  labelText: string;
  displayExpr?: string;
  showLabel?: boolean;
  searchEnabled?: boolean;
  selectDataSource?: any;
  showClearButton?: boolean;
  setFirstRecordOnLoad?: boolean;
  grid: (
    setRecordCb: React.Dispatch<React.SetStateAction<null>>
  ) => JSX.Element;
} & (
  | {
      selectDataSource?: never;
      fetchData: () => AxiosPromiseLike<{ value: TRecord[] }>;
      onFetchSuccess?: (value: TRecord[]) => void;
    }
  | {
      selectDataSource: SelectDataSource;
      fetchData?: never;
      onFetchSuccess?: never;
    }
);

const determineStore = (selectDataSource: SelectDataSource) => {
  if (typeof selectDataSource === 'function') {
    return selectDataSource().store;
  }

  if (XData.isArray(selectDataSource)) {
    return {
      type: 'array',
      data: selectDataSource,
      key: 'Id'
    };
  }

  return selectDataSource.store;
};

const createDs = (selectDataSource: SelectDataSource | undefined) => {
  if (!selectDataSource) return null;

  return new DataSource({
    store: determineStore(selectDataSource)
  });
};

function AdditionSelect<TRecord extends Record>({
  recordId,
  setRecordId,
  fetchData,
  popupTitle,
  labelText,
  onFetchSuccess,
  displayExpr = 'Nazwa',
  showLabel = true,
  searchEnabled = false,
  selectDataSource,
  showClearButton = false,
  setFirstRecordOnLoad = true,
  grid
}: AdditionSelectProps<TRecord>) {
  const selectInstance = useRef(null);
  const selectRecordId = useRef<RecordId>(null);
  const store = useRef(createDs(selectDataSource));

  const [records, setRecords] = useState<TRecord[]>([]);
  const [recordCurrentId, setRecordCurrentId] = useState(null);
  const [showRecordsPopup, setShowRecordsPopup] = useState(false);

  const { t } = useTranslation();
  const recordsSetup = () => {
    if (!fetchData) return;

    fetchData()
      .then(({ data: { value } }) => {
        setRecords(value);

        if (onFetchSuccess) {
          onFetchSuccess(value);
        }

        if (!recordId && setFirstRecordOnLoad) {
          setRecordId(value[0].Id);
        }
      })
      .catch(XUserInteraction.notifyError);
  };

  useOnChangeEffect(() => {
    if (!selectDataSource || showRecordsPopup) return;

    store.current?.reload().then(() => {
      (selectInstance.current as any).repaint();
    });
  }, [showRecordsPopup]);

  useEffect(() => {
    if (!showRecordsPopup) {
      recordsSetup();
    } else if (selectDataSource) {
      selectRecordId.current = recordId || null;
      setRecordId(null);
    }
  }, [showRecordsPopup]);

  const handleOnChange = (e: { itemData?: TRecord }) =>
    setRecordId(e.itemData?.Id || null);

  const handleSelectClear = (e: { value?: RecordId }) => {
    if (e.value === null) {
      setRecordId(null);
    }
  };

  const handleSave = () => {
    setRecordId(recordCurrentId);
    setShowRecordsPopup(false);
  };

  const hidePopup = (e?: { cancel: boolean }) => {
    if (selectDataSource) {
      setRecordId(selectRecordId.current);
      selectRecordId.current = null;
    }

    if (e) {
      e.cancel = false;
    }

    setShowRecordsPopup(false);
  };
  const renderSelect = () => {
    return (
      <div className="select-button-wrapper">
        <SelectBox
          dataSource={
            store.current || {
              store: {
                type: 'array',
                data: records,
                key: 'Id'
              }
            }
          }
          onInitialized={(e) => {
            XDevExtreme.setInstanceToRef(selectInstance, e);
          }}
          showClearButton={showClearButton}
          searchEnabled={searchEnabled}
          onValueChanged={handleSelectClear}
          onItemClick={handleOnChange}
          value={recordId}
          displayExpr={displayExpr}
          valueExpr="Id"
        />
        <Button
          type="normal"
          stylingMode="contained"
          icon="plus"
          onClick={() => setShowRecordsPopup(true)}
        />
      </div>
    );
  };
  return (
    <>
      <DataPopup
        visibiblity={showRecordsPopup}
        setVisibility={setShowRecordsPopup}
        showCloseButton
        title={popupTitle}
        customCloseBtnHandler={hidePopup}
      >
        <div className="popup-content--with-btns hide-popup-root">
          {grid(setRecordCurrentId)}
          <ActionBtns
            customSaveText={t('Ok')}
            handleSave={handleSave}
            handleCancel={hidePopup}
          />
        </div>
      </DataPopup>
      {showLabel ? (
        <LabeledItem labelText={labelText}>{renderSelect()}</LabeledItem>
      ) : (
        renderSelect()
      )}
    </>
  );
}

export default AdditionSelect;
